import { React, useEffect } from "react";
import { Grid, TextField, FormControlLabel, Checkbox, Autocomplete, Stack, Typography, FormHelperText, FormControl, FormGroup, Paper, MenuItem, Select } from "@mui/material";
import {
    stepperLabelSx,
    stepperTextFieldSx,
    stepperTextareaSx,
    stepperAutocompleteInputSx,
} from "./stepperFormStyles";
import { useState } from "react";
import { listTenantSlaAction } from "../../Actions/TenantSlaActions";

const CommonStep = ({ commonFormik, countryData, stateData, getStateData, isEditing, isEditMode }) => {

    const [slaList, setSlaList] = useState([]);
    const [slaLoading, setSlaLoading] = useState(false);

    useEffect(() => {
        const fetchSlaList = async () => {
            try {
                setSlaLoading(true);

                const response = await listTenantSlaAction();

                setSlaList(
                    response?.data?.data || []
                );
            } catch (error) {
                console.error("SLA fetch error:", error);
            } finally {
                setSlaLoading(false);
            }
        };

        fetchSlaList();
    }, []);

    const allProductsSelected = Boolean(
        commonFormik?.values?.isQbot &&
        commonFormik?.values?.isPaycentral &&
        commonFormik?.values?.isKyc &&
        commonFormik?.values?.isTripola
    );

    const isTrialConfigDisabled =
        Boolean(commonFormik?.values?.hasTrialConfiguration) || allProductsSelected;

    useEffect(() => {
        if (commonFormik.values.companyCountry) {
            getStateData(commonFormik.values.companyCountry);
        }
    }, [commonFormik.values.companyCountry]);

    // Trial needs at least one product not already assigned — disable when all are selected
    useEffect(() => {
        if (!allProductsSelected || !commonFormik?.values?.isTrialConfig) return;

        commonFormik.setFieldValue("isTrialConfig", false);
        commonFormik.setFieldValue("trialPortal", []);
        commonFormik.setFieldValue("trialDays", "");
        commonFormik.setFieldValue("graceDays", "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        allProductsSelected,
        commonFormik?.values?.isTrialConfig,
    ]);

    if (!commonFormik || !commonFormik.values) {
        return null; // or a loader
    }

    const selectMenuProps = {
        PaperProps: {
            sx: {
                maxHeight: 320,
                borderRadius: "12px",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                mt: 0.5,
                fontFamily: "var(--app-font)",
            },
        },
    };

    return (
        <>
            {/* Form */}
            <Grid container spacing={2} rowSpacing={2.5}>

                {/* First Name */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        First Name
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        name="firstName"
                        sx={stepperTextFieldSx}
                        value={commonFormik?.values?.firstName}
                        onChange={(e) => {
                            commonFormik.handleChange(e);
                            commonFormik.setFieldTouched("firstName", true, false);
                        }}
                        onBlur={commonFormik.handleBlur}
                        error={commonFormik?.touched?.firstName && Boolean(commonFormik?.errors?.firstName)}
                        helperText={commonFormik?.touched?.firstName && commonFormik?.errors?.firstName}
                    />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        Last Name
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        name="lastName"
                        sx={stepperTextFieldSx}
                        value={commonFormik?.values?.lastName}
                        onChange={(e) => {
                            commonFormik.handleChange(e);
                            commonFormik.setFieldTouched("lastName", true, false);
                        }}
                        onBlur={commonFormik.handleBlur}
                        error={commonFormik?.touched?.lastName && Boolean(commonFormik?.errors?.lastName)}
                        helperText={commonFormik?.touched?.lastName && commonFormik?.errors?.lastName}
                    />
                </Grid>

                {/* Email */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        Email
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        name="email"
                        sx={stepperTextFieldSx}
                        value={commonFormik?.values?.email}
                        onChange={(e) => {
                            commonFormik.handleChange(e);
                            commonFormik.setFieldTouched("email", true, false);
                        }}
                        onBlur={commonFormik.handleBlur}
                        error={commonFormik?.touched?.email && Boolean(commonFormik?.errors?.email)}
                        helperText={commonFormik?.touched?.email && commonFormik?.errors?.email}
                    />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        Company Phone Number
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        name="companyPhoneNumber"
                        sx={stepperTextFieldSx}
                        value={commonFormik?.values?.companyPhoneNumber}
                        onChange={(e) => {
                            commonFormik.handleChange(e);
                            commonFormik.setFieldTouched("companyPhoneNumber", true, false);
                        }}

                        onBlur={commonFormik.handleBlur}
                        error={
                            commonFormik?.touched?.companyPhoneNumber &&
                            Boolean(commonFormik?.errors?.companyPhoneNumber)
                        }
                        helperText={
                            commonFormik?.touched?.companyPhoneNumber &&
                            commonFormik?.errors?.companyPhoneNumber
                        }
                    />
                </Grid>

                {/* Country */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        Country
                    </Typography>
                    <Autocomplete
                        options={countryData || []}
                        getOptionLabel={(option) => option?.name || ""}
                        value={
                            (countryData || []).find(
                                (option) => option.name === commonFormik?.values?.companyCountry
                            ) || null
                        }
                        onChange={(event, value) => {
                            commonFormik.setFieldValue("companyCountry", value?.name || "");
                            commonFormik.setFieldValue("companyState", "");
                            getStateData(value?.name);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                sx={stepperAutocompleteInputSx}
                                error={
                                    commonFormik?.touched?.companyCountry &&
                                    Boolean(commonFormik?.errors?.companyCountry)
                                }
                                helperText={
                                    commonFormik?.touched?.companyCountry &&
                                    commonFormik?.errors?.companyCountry
                                }
                            />
                        )}
                    />
                </Grid>

                {/* State */}
                <Grid item xs={12} md={6}>
                    <Typography sx={stepperLabelSx}>
                        State
                    </Typography>
                    <Autocomplete
                        options={stateData || []}
                        getOptionLabel={(option) => option?.name || ""}
                        value={
                            (stateData || []).find(
                                (option) => option.name === commonFormik?.values?.companyState
                            ) ?? null
                        }
                        onChange={(event, value) =>
                            commonFormik.setFieldValue("companyState", value?.name || "")
                        }
                        disabled={!stateData.length}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                sx={stepperAutocompleteInputSx}
                                error={
                                    commonFormik?.touched?.companyState &&
                                    Boolean(commonFormik?.errors?.companyState)
                                }
                                helperText={
                                    commonFormik?.touched?.companyState &&
                                    commonFormik?.errors?.companyState
                                }
                            />
                        )}
                    />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                    <Typography sx={stepperLabelSx}>
                        Company Address
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        name="companyAddress"
                        sx={{ ...stepperTextareaSx, mb: 2 }}
                        value={commonFormik?.values?.companyAddress}
                        onChange={(e) => {
                            commonFormik.handleChange(e);
                            commonFormik.setFieldTouched("companyAddress", true, false);
                        }}
                        onBlur={commonFormik.handleBlur}
                        error={
                            commonFormik?.touched?.companyAddress &&
                            Boolean(commonFormik?.errors?.companyAddress)
                        }
                        helperText={
                            commonFormik?.touched?.companyAddress &&
                            commonFormik?.errors?.companyAddress
                        }
                    />
                </Grid>

                {/* Products */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            backgroundColor: "#fafbff",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                mb: 1.5,
                                color: "#1f2937",
                            }}
                        >
                            Current Products
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isQbot"
                                        checked={commonFormik?.values?.isQbot}
                                        disabled={isEditMode}
                                        onChange={(e) =>
                                            commonFormik.setFieldValue(
                                                "isQbot",
                                                e.target.checked
                                            )
                                        }
                                        sx={{
                                            color: "#25429b",
                                            "&.Mui-checked": {
                                                color: "#25429b",
                                            },
                                        }}
                                    />
                                }
                                label="QBot"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isPaycentral"
                                        checked={commonFormik?.values?.isPaycentral}
                                        disabled={isEditMode}
                                        onChange={(e) =>
                                            commonFormik.setFieldValue(
                                                "isPaycentral",
                                                e.target.checked
                                            )
                                        }
                                        sx={{
                                            color: "#25429b",
                                            "&.Mui-checked": {
                                                color: "#25429b",
                                            },
                                        }}
                                    />
                                }
                                label="PayCentral"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isKyc"
                                        checked={commonFormik?.values?.isKyc}
                                        disabled={isEditMode}
                                        onChange={(e) =>
                                            commonFormik.setFieldValue(
                                                "isKyc",
                                                e.target.checked
                                            )
                                        }
                                        sx={{
                                            color: "#25429b",
                                            "&.Mui-checked": {
                                                color: "#25429b",
                                            },
                                        }}
                                    />
                                }
                                label="KYC"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isTripola"
                                        checked={commonFormik?.values?.isTripola}
                                        disabled={isEditMode}
                                        onChange={(e) =>
                                            commonFormik.setFieldValue(
                                                "isTripola",
                                                e.target.checked
                                            )
                                        }
                                        sx={{
                                            color: "#25429b",
                                            "&.Mui-checked": {
                                                color: "#25429b",
                                            },
                                        }}
                                    />
                                }
                                label="Tripola"
                            />
                        </Stack>

                        {commonFormik?.touched?.atLeastOneProduct &&
                            commonFormik?.errors?.atLeastOneProduct && (
                                <FormHelperText>
                                    {commonFormik.errors.atLeastOneProduct}
                                </FormHelperText>
                            )}
                    </Paper>
                </Grid>

                {/* SLA */}
                {!isEditMode && (
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: "1px solid #e5e7eb",
                                borderRadius: "12px",
                                backgroundColor: "#fafbff",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    mb: 1.5,
                                    color: "#1f2937",
                                }}
                            >
                                SLA 
                            </Typography>

                            <TextField
                                select
                                fullWidth
                                size="small"
                                name="slaId"
                                value={commonFormik.values.slaId || ""}
                                onChange={(e) =>
                                    commonFormik.setFieldValue(
                                        "slaId",
                                        e.target.value
                                    )
                                }
                                onBlur={commonFormik.handleBlur}
                                error={
                                    commonFormik.touched.slaId &&
                                    Boolean(commonFormik.errors.slaId)
                                }
                                helperText={
                                    commonFormik.touched.slaId &&
                                    commonFormik.errors.slaId
                                }
                                sx={{
                                    ...stepperTextFieldSx,
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff",
                                        borderRadius: "8px",
                                    },
                                }}
                                SelectProps={{
                                    displayEmpty: true,
                                    MenuProps: selectMenuProps,
                                    inputProps: {
                                        "aria-label": "SLA",
                                    },
                                    renderValue: (selected) => {
                                        if (!selected) {
                                            return slaLoading
                                                ? "Loading SLA..."
                                                : "Select SLA";
                                        }

                                        const selectedSla = slaList.find(
                                            (sla) =>
                                                String(sla.id) === String(selected)
                                        );

                                        return selectedSla?.slaName || "Select SLA";
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    {slaLoading ? "Loading SLA..." : "Select SLA"}
                                </MenuItem>

                                {slaList.map((sla) => (
                                    <MenuItem
                                        key={sla.id}
                                        value={sla.id}
                                    >
                                        {sla.slaName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Paper>
                    </Grid>
                )}

                {/* Trial Configuration */}
                {isEditMode && (
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: "1px solid #e5e7eb",
                                borderRadius: "12px",
                                backgroundColor: "#fafbff",
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={commonFormik.values.isTrialConfig}
                                        disabled={isTrialConfigDisabled}
                                        onChange={(e) => {
                                            commonFormik.setFieldValue(
                                                "isTrialConfig",
                                                e.target.checked
                                            );
                                            commonFormik.setFieldTouched("trialPortal", true);

                                            if (!e.target.checked) {
                                                commonFormik.setFieldValue("trialPortal", []);
                                                commonFormik.setFieldValue("trialDays", "");
                                                commonFormik.setFieldValue("graceDays", "");
                                            }
                                        }}
                                        sx={{
                                            color: "#25429b",
                                            "&.Mui-checked": {
                                                color: "#25429b",
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: isTrialConfigDisabled
                                                ? "#94a3b8"
                                                : "#1f2937",
                                        }}
                                    >
                                        Trial Configuration
                                    </Typography>
                                }
                            />
                            {allProductsSelected &&
                                !commonFormik.values?.hasTrialConfiguration && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#64748b",
                                            ml: 1,
                                            mt: 0.5,
                                            fontSize: 12.5,
                                        }}
                                    >
                                        Trial is unavailable because all current products are already selected.
                                    </Typography>
                                )}

                            {commonFormik.values.isTrialConfig && (
                                <>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#6b7280",
                                            mb: 2,
                                            ml: 1,
                                        }}
                                    >
                                        Select the trial portal for this client
                                    </Typography>

                                    <FormGroup
                                        row
                                        sx={{
                                            "& .MuiFormControlLabel-root": {
                                                margin: 0,
                                            },
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            flexWrap="wrap"
                                        >
                                            {(!commonFormik.values.isQbot || commonFormik.values.trialPortal?.includes("qbot")) && (
                                                <Paper
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: "8px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        border:
                                                            commonFormik.values.trialPortal?.includes("qbot")
                                                                ? "2px solid #25429b"
                                                                : "1px solid #d1d5db",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="qbot"
                                                        control={
                                                            <Checkbox
                                                                disabled={commonFormik.values?.hasTrialConfiguration}
                                                                checked={commonFormik.values.trialPortal?.includes("qbot")}
                                                                onChange={(e) => {
                                                                    const current = commonFormik.values.trialPortal || [];
                                                                    if (e.target.checked) {
                                                                        commonFormik.setFieldValue("trialPortal", [...current, "qbot"]);
                                                                    } else {
                                                                        commonFormik.setFieldValue("trialPortal", current.filter((x) => x !== "qbot"));
                                                                    }
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    p: 0.25,
                                                                    "& .MuiSvgIcon-root": {
                                                                        fontSize: 18,
                                                                    },
                                                                    color: "#25429b",
                                                                    "&.Mui-checked": {
                                                                        color: "#25429b",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="QBot"
                                                        sx={{
                                                            m: 0,
                                                            gap: 0.5,
                                                            alignItems: "center",

                                                            "& .MuiTypography-root": {
                                                                fontSize: "14px",
                                                                lineHeight: 1,
                                                            },

                                                            "& .MuiButtonBase-root": {
                                                                padding: "2px",
                                                            },
                                                        }}
                                                    />
                                                </Paper>
                                            )}

                                            {(!commonFormik.values.isPaycentral || commonFormik.values.trialPortal?.includes("paycentral")) && (
                                                <Paper
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: "10px",
                                                        border:
                                                            commonFormik.values.trialPortal?.includes("paycentral")
                                                                ? "2px solid #25429b"
                                                                : "1px solid #d1d5db",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="paycentral"
                                                        control={
                                                            <Checkbox size="small"
                                                                disabled={commonFormik.values?.hasTrialConfiguration}
                                                                checked={commonFormik.values.trialPortal?.includes("paycentral")}
                                                                onChange={(e) => {
                                                                    const current = commonFormik.values.trialPortal || [];
                                                                    if (e.target.checked) {
                                                                        commonFormik.setFieldValue("trialPortal", [...current, "paycentral"]);
                                                                    } else {
                                                                        commonFormik.setFieldValue("trialPortal", current.filter((x) => x !== "paycentral"));
                                                                    }
                                                                }}
                                                                sx={{
                                                                    p: 0.25,
                                                                    "& .MuiSvgIcon-root": {
                                                                        fontSize: 18,
                                                                    },
                                                                    color: "#25429b",
                                                                    "&.Mui-checked": {
                                                                        color: "#25429b",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="PayCentral"
                                                        sx={{
                                                            m: 0,
                                                            gap: 0.5,
                                                            alignItems: "center",

                                                            "& .MuiTypography-root": {
                                                                fontSize: "14px",
                                                                lineHeight: 1,
                                                            },

                                                            "& .MuiButtonBase-root": {
                                                                padding: "2px",
                                                            },
                                                        }}
                                                    />
                                                </Paper>
                                            )}

                                            {(!commonFormik.values.isKyc || commonFormik.values.trialPortal?.includes("kyc")) && (
                                                <Paper
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: "10px",
                                                        border:
                                                            commonFormik.values.trialPortal?.includes("kyc")
                                                                ? "2px solid #25429b"
                                                                : "1px solid #d1d5db",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="kyc"
                                                        control={
                                                            <Checkbox size="small"
                                                                disabled={commonFormik.values?.hasTrialConfiguration}
                                                                checked={commonFormik.values.trialPortal?.includes("kyc")}
                                                                onChange={(e) => {
                                                                    const current = commonFormik.values.trialPortal || [];
                                                                    if (e.target.checked) {
                                                                        commonFormik.setFieldValue("trialPortal", [...current, "kyc"]);
                                                                    } else {
                                                                        commonFormik.setFieldValue("trialPortal", current.filter((x) => x !== "kyc"));
                                                                    }
                                                                }}
                                                                sx={{
                                                                    p: 0.25,
                                                                    "& .MuiSvgIcon-root": {
                                                                        fontSize: 18,
                                                                    },
                                                                    color: "#25429b",
                                                                    "&.Mui-checked": {
                                                                        color: "#25429b",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="KYC"
                                                        sx={{
                                                            m: 0,
                                                            gap: 0.5,
                                                            alignItems: "center",

                                                            "& .MuiTypography-root": {
                                                                fontSize: "14px",
                                                                lineHeight: 1,
                                                            },

                                                            "& .MuiButtonBase-root": {
                                                                padding: "2px",
                                                            },
                                                        }}
                                                    />
                                                </Paper>
                                            )}

                                            {(!commonFormik.values.isTripola || commonFormik.values.trialPortal?.includes("tripola")) && (
                                                <Paper
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: "10px",
                                                        border:
                                                            commonFormik.values.trialPortal?.includes("tripola")
                                                                ? "2px solid #25429b"
                                                                : "1px solid #d1d5db",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="tripola"
                                                        control={
                                                            <Checkbox size="small"
                                                                disabled={commonFormik.values?.hasTrialConfiguration}
                                                                checked={commonFormik.values.trialPortal?.includes("tripola")}
                                                                onChange={(e) => {
                                                                    const current = commonFormik.values.trialPortal || [];
                                                                    if (e.target.checked) {
                                                                        commonFormik.setFieldValue("trialPortal", [...current, "tripola"]);
                                                                    } else {
                                                                        commonFormik.setFieldValue("trialPortal", current.filter((x) => x !== "tripola"));
                                                                    }
                                                                }}
                                                                sx={{
                                                                    p: 0.25,
                                                                    "& .MuiSvgIcon-root": {
                                                                        fontSize: 18,
                                                                    },
                                                                    color: "#25429b",
                                                                    "&.Mui-checked": {
                                                                        color: "#25429b",
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="Tripola"
                                                        sx={{
                                                            m: 0,
                                                            gap: 0.5,
                                                            alignItems: "center",

                                                            "& .MuiTypography-root": {
                                                                fontSize: "14px",
                                                                lineHeight: 1,
                                                            },

                                                            "& .MuiButtonBase-root": {
                                                                padding: "2px",
                                                            },
                                                        }}
                                                    />
                                                </Paper>
                                            )}
                                        </Stack>
                                    </FormGroup>
                                    {commonFormik.touched.trialPortal &&
                                        commonFormik.errors.trialPortal && (
                                            <FormHelperText error sx={{ ml: 1 }}>
                                                {commonFormik.errors.trialPortal}
                                            </FormHelperText>
                                        )}
                                </>
                            )}
                            {commonFormik.values.isTrialConfig && (
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    {(Array.isArray(commonFormik.values.trialPortal)
                                        ? commonFormik.values.trialPortal
                                        : commonFormik.values.trialPortal
                                            ? [commonFormik.values.trialPortal]
                                            : []
                                    ).map((portal) => (
                                        <Grid item xs={12} md={6} key={portal}>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    mb: 0.5,
                                                    color: "#1f2937",
                                                    textTransform: "capitalize",
                                                }}
                                            >
                                                Trial Days for {portal === "paycentral" ? "PayCentral" : portal.charAt(0).toUpperCase() + portal.slice(1)}
                                            </Typography>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                name={`trialDays.${portal}`}
                                                placeholder={`Enter trial days for ${portal}`}
                                                value={
                                                    typeof commonFormik.values.trialDays === "object"
                                                        ? commonFormik.values.trialDays?.[portal] || ""
                                                        : portal === commonFormik.values.trialPortal?.[0]
                                                            ? commonFormik.values.trialDays || ""
                                                            : ""
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const currentTrialDays = typeof commonFormik.values.trialDays === "object"
                                                        ? commonFormik.values.trialDays
                                                        : {};

                                                    commonFormik.setFieldValue("trialDays", {
                                                        ...currentTrialDays,
                                                        [portal]: value ? parseInt(value) : "",
                                                    });
                                                }}
                                                disabled={commonFormik.values?.hasTrialConfiguration}
                                                error={
                                                    commonFormik.touched.trialDays &&
                                                    Boolean(
                                                        typeof commonFormik.errors.trialDays === "object"
                                                            ? commonFormik.errors.trialDays?.[portal]
                                                            : commonFormik.errors.trialDays
                                                    )
                                                }
                                                helperText={
                                                    commonFormik.touched.trialDays &&
                                                    (typeof commonFormik.errors.trialDays === "object"
                                                        ? commonFormik.errors.trialDays?.[portal]
                                                        : commonFormik.errors.trialDays)
                                                }
                                            />
                                        </Grid>
                                    ))}

                                    {commonFormik.values?.hasTrialConfiguration && (
                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    mb: 0.5,
                                                    color: "#1f2937",
                                                }}
                                            >
                                                Grace Period (Days)
                                            </Typography>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                name="graceDays"
                                                placeholder="Enter grace days"
                                                value={commonFormik.values.graceDays ?? ""}
                                                onChange={commonFormik.handleChange}
                                                error={
                                                    commonFormik.touched.graceDays &&
                                                    Boolean(commonFormik.errors.graceDays)
                                                }
                                                helperText={
                                                    commonFormik.touched.graceDays &&
                                                    commonFormik.errors.graceDays
                                                }
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                )}

            </Grid>
        </>
    );
};

export default CommonStep;